import Peer, { DataConnection } from 'peerjs';
import { toast } from 'sonner';
import { useSessionStore } from '../stores/session';

const PEERJS_HOST = 'peer.yage.games';
const PEERJS_PORT = 443;
const PEERJS_PATH = '/';

export class PeerAuthError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PeerAuthError';
    }
}

class PeerManager {
    private peer: Peer | null = null;
    private conn: DataConnection | null = null;
    private status: 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'error' = 'disconnected';

    private static CHUNK_SIZE = 4 * 1024; // 4KB
    private chunkAssemblers: Map<string, { total: number; chunks: (string | undefined)[]; }> = new Map();
    private apiRequestPromises: Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void; }> = new Map();

    private wsEventListeners: Map<string, Set<(data: any) => void>> = new Map();

    private persistentPeerId: string | null = null;
    private token: string | null = null;

    constructor() {
        this.wsEventListeners.set('message', new Set());
        this.wsEventListeners.set('open', new Set());
        this.wsEventListeners.set('close', new Set());
        this.wsEventListeners.set('error', new Set());
    }

    public getStatus() {
        return this.status;
    }

    public isConnectedToPeer(peerId: string): boolean {
        return this.status === 'connected' && this.persistentPeerId === peerId && this.conn !== null;
    }

    private updateStatus(newStatus: typeof this.status) {
        this.status = newStatus;
        console.log(`[PeerManager] Status changed to: ${newStatus}`);
    }

    public async initiateHandshake(otp: string): Promise<void> {
        this.updateStatus('connecting');
        const tempPeerId = `sprocket-otp-${otp.toUpperCase()}`;

        return new Promise((resolve, reject) => {
            this.destroyPeer();

            const localPeer = new Peer({
                host: PEERJS_HOST,
                port: PEERJS_PORT,
                path: PEERJS_PATH,
            });

            localPeer.on('open', (id) => {
                console.log('[PeerManager] Local peer opened:', id);
                console.log(`[PeerManager] Attempting to connect to temporary peer: ${tempPeerId}`);
                const handshakeConn = localPeer.connect(tempPeerId, {
                    reliable: true,
                    serialization: 'json'
                });

                handshakeConn.on('open', () => {
                    console.log(`[PeerManager] Connection to ${tempPeerId} opened.`);
                    handshakeConn.send({ type: 'init-handshake' });
                });

                handshakeConn.on('data', async (data: any) => {
                    let parsedData = data;
                    if (typeof data === 'string') {
                        try { parsedData = JSON.parse(data); }
                        catch (e) { console.error('[PeerManager] Failed to parse handshake JSON:', e); return; }
                    }

                    if (parsedData.type === 'auth-success') {
                        console.log('[PeerManager] Handshake successful.');
                        this.token = parsedData.token;
                        try {
                            // The peerId is now in the 'aud' claim of the JWT
                            const payloadBase64 = this.token!.split('.')[1];
                            const decodedJson = atob(payloadBase64);
                            const decodedPayload = JSON.parse(decodedJson);
                            this.persistentPeerId = decodedPayload.aud;

                            if (!this.persistentPeerId) {
                                throw new Error("Peer ID (aud) not found in JWT payload.");
                            }
                            localStorage.setItem('webrtc_token', this.token!);
                            localStorage.setItem('webrtc_peer_id', this.persistentPeerId!);

                            await this.connectWithPersistentId(this.persistentPeerId!, this.token!);
                            resolve();
                        } catch (error: Error | any) {
                            reject(new Error(`Failed to process auth token: ${error.message}`));
                        }

                    } else if (parsedData.type === 'auth-fail') {
                        console.error('[PeerManager] Handshake failed:', parsedData.error);
                        reject(new PeerAuthError(parsedData.error || 'Authentication failed.'));
                    }
                    handshakeConn.close();
                    localPeer.destroy();
                });

                handshakeConn.on('error', (err) => {
                    console.error('[PeerManager] Handshake connection error:', err);
                    reject(err);
                    localPeer.destroy();
                });
            });

            localPeer.on('error', (err) => {
                console.error('[PeerManager] Local peer error:', err);
                reject(err);
                this.updateStatus('error');
            });
        });
    }

    public async connectWithPersistentId(peerId: string, token: string): Promise<void> {
        this.persistentPeerId = peerId;
        this.token = token;
        this.updateStatus('authenticating');

        return new Promise((resolve, reject) => {
            let settled = false;
            const resolveOnce = () => {
                if (!settled) {
                    settled = true;
                    console.log('[PeerManager] Resolving connection promise.');
                    resolve();
                }
            };
            const rejectOnce = (err: Error) => {
                if (!settled) {
                    settled = true;
                    console.error('[PeerManager] Rejecting connection promise.', err);
                    reject(err);
                }
            };

            this.destroyPeer();

            this.peer = new Peer({
                host: PEERJS_HOST,
                port: PEERJS_PORT,
                path: PEERJS_PATH,
            });

            this.peer.on('open', (id) => {
                console.log(`[PeerManager] Re-opened local peer as ${id}, connecting to persistent peer ${peerId}`);
                this.conn = this.peer!.connect(peerId, {
                    reliable: true,
                    serialization: 'json'
                });

                this.conn.on('open', () => {
                    console.log(`[PeerManager] Connection to persistent peer ${peerId} opened.`);
                    this.send({ type: 'auth', payload: { token } });
                });

                this.conn.on('data', (data: any) => {
                    let parsedData = data;
                    if (typeof data === 'string') {
                        try { parsedData = JSON.parse(data); }
                        catch (e) {
                            console.error('[PeerManager] Failed to parse persistent connection JSON:', e);
                            return;
                        }
                    }

                    switch (parsedData.type) {
                      case 'auth-success':
                      case 'token-rotated': // Keep for future compatibility
                        this.token = parsedData.newToken;
                        if (data.type === 'token-rotated') {
                            this.token = parsedData.newToken;
                            localStorage.setItem('webrtc_token', this.token!);
                        }
                        resolveOnce();
                        this.updateStatus('connected');
                        toast.success("Secure P2P connection established.");
                        break;
                      case 'auth-fail':
                        rejectOnce(new PeerAuthError(parsedData.error || 'Authentication failed.'));
                        toast.error("P2P Authentication Failed", { description: parsedData.error });
                        this.handleDisconnect();
                        this.handleData(parsedData);
                        break;
                      default:
                        this.handleData(parsedData);
                        break;
                    }
                });
                this.conn.on('close', () => {
                    if (this.getStatus() !== 'connected') {
                        console.error('[PeerManager] Persistent connection closed before completing authentication.');
                        this.handleDisconnect();
                        rejectOnce(new Error('Connection closed before authentication.'));
                    } else {
                        this.handleDisconnect();
                    }
                });
                this.conn.on('error', (err) => {

                    console.error('[PeerManager] Persistent connection error:', err);
                    this.handleDisconnect();
                    rejectOnce(err);
                });
            });
            this.peer.on('error', (err) => {
                 console.error('[PeerManager] Main peer error:', err);
                 this.handleDisconnect();
                 rejectOnce(err);
            });
        });
    }

    private handleData(data: any) {
        if (data.type === 'chunk') {
            this.handleChunk(data.payload);
            return;
        }

        switch(data.type) {
            case 'api-response':
                this.handleApiResponse(data.payload);
                break;
            case 'ws-message':
                this.emitWsEvent('message', { data: data.payload.data });
                break;
            case 'ws-close':
                this.emitWsEvent('close', {});
                break;
        }
    }

    private handleChunk(payload: { id: string; index: number; total: number; data: string; }) {
        const { id, index, total, data } = payload;
        if (!id || typeof index !== 'number' || typeof total !== 'number' || typeof data !== 'string') {
            console.error('[PeerManager] Received invalid chunk payload:', payload);
            return;
        }

        let assembler = this.chunkAssemblers.get(id);
        if (!assembler) {
            assembler = {
                total,
                chunks: new Array(total).fill(undefined),
            };
            this.chunkAssemblers.set(id, assembler);
        }

        assembler.chunks[index] = data;


        // Check if complete
        if (assembler.chunks.every(c => c !== undefined)) {
            const fullMessageStr = assembler.chunks.join('');
            this.chunkAssemblers.delete(id); // Clean up
            try {
                const reassembledData = JSON.parse(fullMessageStr);
                this.handleData(reassembledData);
            } catch (e) {
                console.error('[PeerManager] Failed to parse reassembled message:', e);
            }
        }
    }

    private handleApiResponse(payload: any) {
        const promiseCallbacks = this.apiRequestPromises.get(payload.id);
        if (promiseCallbacks) {
            if (payload.ok) {
                const headers = payload.headers || {};

                // Case-insensitive search for 'set-cookie' and process it
                const setCookieKey = Object.keys(headers).find(k => k.toLowerCase() === 'set-cookie');
                if (setCookieKey) {
                    const setCookieHeader = headers[setCookieKey];
                    const cookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
                    cookies.forEach(cookieString => {
                        document.cookie = cookieString;
                        console.log(`[PeerManager] Set cookie from response: ${cookieString.split(';')[0]}`);
                    });
                    // Remove the header so it's not processed by `new Headers()` incorrectly
                    delete headers[setCookieKey];
                }

                const response = {
                    ok: payload.ok,
                    status: payload.status,
                    statusText: payload.statusText,
                    headers: new Headers(headers),
                    json: async () => JSON.parse(payload.body),
                    text: async () => payload.body,
                };
                promiseCallbacks.resolve(response);
            } else {
                promiseCallbacks.reject(new Error(payload.error || payload.body || 'Proxy request failed'));
            }
            this.apiRequestPromises.delete(payload.id);
        }
    }

    public proxyRequest(url: string, init: RequestInit = {}): Promise<any> { // The 'any' should be a Response-like object
        if (this.status !== 'connected' || !this.conn) {
            const errorResponse = {
                ok: false,
                status: 403,
                statusText: 'Forbidden',
                headers: new Headers({ 'Content-Type': 'application/json' }),
                json: async () => ({ detail: 'WebRTC connection not active.' }),
                text: async () => JSON.stringify({ detail: 'WebRTC connection not active.' }),
            };
            return Promise.resolve(errorResponse);
        }

        const requestId = `${Date.now()}-${Math.random()}`;

        // Prepare headers, manually adding cookies. This mimics browser's `fetch` behavior.
        const headers = new Headers(init.headers);
        if (document.cookie) {
            headers.set('cookie', document.cookie);
        }

        const payload = {
            id: requestId,
            url,
            init: {
                method: init.method,
                headers: Object.fromEntries(headers.entries()),
                body: init.body,
            },
        };

        return new Promise((resolve, reject) => {
            this.apiRequestPromises.set(requestId, { resolve, reject });
            this.send({ type: 'api-request', payload });
        });
    }

    private send(data: any): void {
        if (!this.conn) {
            throw new Error("No active connection to send data.");
        }

        const messageStr = JSON.stringify(data);

        if (messageStr.length > PeerManager.CHUNK_SIZE) {
            const chunkId = `${Date.now()}-${Math.random()}`;
            const totalChunks = Math.ceil(messageStr.length / PeerManager.CHUNK_SIZE);

            for (let i = 0; i < totalChunks; i++) {
                const chunkData = messageStr.substring(i * PeerManager.CHUNK_SIZE, (i + 1) * PeerManager.CHUNK_SIZE);
                const chunkMessage = {
                    type: 'chunk',
                    payload: {
                        id: chunkId, index: i, total: totalChunks, data: chunkData,
                    }
                };
                this.conn.send(chunkMessage);
            }
        } else {
            this.conn.send(data);
        }
    }

    private handleDisconnect() {
        this.updateStatus('disconnected');
        this.destroyPeer();
        useSessionStore.getState().setCommunicationMode('direct');
        toast.error("P2P connection lost. You may need to refresh.");
    }

    private destroyPeer() {
        if (this.conn) {
            this.conn.close();
            this.conn = null;
        }
        if (this.peer) {
            this.peer.destroy();
            this.peer = null;
        }
    }

    // --- WebSocket Proxying ---
    public sendWsMessage(path: string, data: any) {
        this.send({ type: 'ws-send', payload: { path, data }});
    }

    public connectWs(path: string) {
        const headers = {
            'cookie': document.cookie || '',
        };
        this.send({ type: 'ws-connect', payload: { path, headers }});
        this.emitWsEvent('open', {});
    }

    public closeWs(path: string) {
        this.send({ type: 'ws-close', payload: { path }});
    }

    public onWs(event: 'message' | 'open' | 'close' | 'error', callback: (data: any) => void) {
        this.wsEventListeners.get(event)?.add(callback);
    }

    public offWs(event: 'message' | 'open' | 'close' | 'error', callback: (data: any) => void) {
        this.wsEventListeners.get(event)?.delete(callback);
    }

    private emitWsEvent(event: string, data: any) {
        this.wsEventListeners.get(event)?.forEach(cb => cb(data));
    }
}

const peerManager = new PeerManager();
export default peerManager;
